import React from 'react';
import { RefreshCw, Wifi, WifiOff, Database, TrendingUp } from 'lucide-react';
import { Tooltip } from '../components/ui/Tooltip';
import { useCountryData } from '../hooks';

// Import new overview panels
import { HeroPanel } from '../components/panels/overview/HeroPanel';
import { InvestmentThesisPanel } from '../components/panels/overview/InvestmentThesisPanel';
import { CreditMomentumPanel } from '../components/panels/overview/CreditMomentumPanel';
import { DigitalEconomyPanel } from '../components/panels/overview/DigitalEconomyPanel';
import { StrategicCatalystsPanel } from '../components/panels/overview/StrategicCatalystsPanel';
import { DemographicAdvantagePanel } from '../components/panels/overview/DemographicAdvantagePanel';

// Import existing panels we still want
import { SectorPanel } from '../components/panels/SectorPanel';
import { PeerPanel } from '../components/panels/PeerPanel';

// Data Source Indicator
const DataSourceIndicator: React.FC<{
  source: 'live' | 'cached' | 'static' | null;
  loading: boolean;
  onRefresh: () => void;
}> = ({ source, loading, onRefresh }) => {
  const getSourceInfo = () => {
    switch (source) {
      case 'live':
        return { icon: Wifi, color: 'text-data-green', label: 'Live' };
      case 'cached':
        return { icon: Database, color: 'text-data-amber', label: 'Cached' };
      case 'static':
        return { icon: WifiOff, color: 'text-gray-500', label: 'Static' };
      default:
        return { icon: WifiOff, color: 'text-gray-500', label: 'Loading' };
    }
  };

  const { icon: Icon, color, label } = getSourceInfo();

  return (
    <div className="flex items-center gap-2">
      <Tooltip content={`Data source: ${label}`} position="bottom">
        <div className={`flex items-center gap-1 ${color}`}>
          <Icon size={12} />
          <span className="text-[9px] uppercase">{label}</span>
        </div>
      </Tooltip>
      <Tooltip content="Refresh data" position="bottom">
        <button
          onClick={onRefresh}
          disabled={loading}
          className="p-1 hover:bg-terminal-border rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw size={12} className={`text-gray-400 hover:text-white ${loading ? 'animate-spin' : ''}`} />
        </button>
      </Tooltip>
    </div>
  );
};

const OverviewView: React.FC = () => {
  // Fetch live country data
  const { data, loading, error, refresh, dataSource } = useCountryData('UZB');

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-terminal-bg">
      {/* Error Banner */}
      {error && (
        <div className="bg-data-red/20 border-b border-data-red/50 px-3 py-1.5 flex items-center justify-between shrink-0">
          <span className="text-[10px] text-data-red">Failed to load live data: {error}</span>
          <button
            onClick={refresh}
            className="text-[10px] text-data-red hover:text-white transition-colors underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-3 lg:p-4">
        {/* Hero Section - Full Width */}
        <div className="mb-4">
          <HeroPanel />
        </div>

        {/* Main Grid - Investment Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-4">
          {/* Investment Thesis - Key selling points */}
          <div className="md:col-span-1 lg:col-span-1">
            <InvestmentThesisPanel />
          </div>

          {/* Credit Momentum - Upgrade story */}
          <div className="md:col-span-1 lg:col-span-1">
            <CreditMomentumPanel />
          </div>

          {/* Strategic Catalysts - WTO and events */}
          <div className="md:col-span-2 lg:col-span-1">
            <StrategicCatalystsPanel />
          </div>
        </div>

        {/* Secondary Grid - Market Dynamics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-4">
          {/* Digital Economy - Unicorns */}
          <div className="md:col-span-1">
            <DigitalEconomyPanel />
          </div>

          {/* Demographic Advantage */}
          <div className="md:col-span-1">
            <DemographicAdvantagePanel />
          </div>

          {/* Sector Opportunities */}
          <div className="md:col-span-2 lg:col-span-1">
            <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden h-full">
              <div className="px-4 py-3 border-b border-terminal-border bg-terminal-bg/50">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-data-green" />
                  <h2 className="text-sm font-bold text-white uppercase tracking-wide">Sector Opportunities</h2>
                </div>
              </div>
              <div className="p-2">
                <SectorPanel isMinimized={false} />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row - Peer Comparison */}
        <div className="grid grid-cols-1 gap-3 lg:gap-4">
          <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-terminal-border bg-terminal-bg/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-data-amber" />
                  <h2 className="text-sm font-bold text-white uppercase tracking-wide">Peer Comparison</h2>
                </div>
                <span className="text-[10px] text-gray-500">Uzbekistan vs Emerging Markets</span>
              </div>
            </div>
            <div className="p-2">
              <PeerPanel isMinimized={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Data Source Footer */}
      <div className="h-7 bg-terminal-header border-t border-terminal-border flex items-center justify-between px-3 shrink-0">
        <div className="flex items-center gap-4">
          <span className="text-[9px] text-gray-500">
            UZBEKISTAN (UZB) | Central Asia Investment Terminal
          </span>
          <span className="text-[9px] text-data-green font-medium">
            Real-time market intelligence
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[9px] text-gray-500">
            Last updated: {data?.lastUpdated?.toLocaleString() || new Date().toLocaleString()}
          </span>
          <DataSourceIndicator source={dataSource} loading={loading} onRefresh={refresh} />
        </div>
      </div>
    </div>
  );
};

export default OverviewView;
